import React from 'react'
import {
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, ContactItemSkeletonList } from '@components'
import { useContactMappingStore } from '@store'
import { useLanguage } from '@context'
import { AddContactButton } from '../../components/AddContactButton'
import { Contact } from '@hooks'
import { defaultImage } from '@assets'

type AddContactsListProps = {
  contacts: Contact[]
  onContactPress?: (contact: Contact) => void
  onAddPress?: (contact: Contact) => void
  pendingRequestAccountIds?: Set<string>
  emptyMessage?: string
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
}

const AVATAR_SIZE = 50
const ITEM_HEIGHT = 70

export const ContactsList: React.FC<AddContactsListProps> = ({
  contacts,
  onContactPress,
  onAddPress,
  pendingRequestAccountIds,
  emptyMessage,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const resolvedEmptyMessage = emptyMessage || t('contactsScreen.noContactFound')

  const getMutualContactsText = (contactId: string): string => {
    //const mutualCount = getCommonContacts(contactId).length
    let mutualCount = 0 //TODO
    return `${mutualCount || 'Pas de'} contacts en commun`
  }

  const getDegreeLabel = (degree: number): string => {
    if (degree === 1) return t('addContact.degree1')
    if (degree === 2) return t('addContact.degree2')
    if (degree === 3) return t('addContact.degree3')
    return ''
  }

  const renderContactItem = ({ item: contact }: { item: Contact }) => {
    const isContact = false //TODO
    const isRequestPending = pendingRequestAccountIds?.has(contact.accountId) ?? false
    const degree = contact.degree
    const degreeLabel = degree != null ? getDegreeLabel(degree) : ''
    return (
      <TouchableOpacity
        style={styles.contactItem}
        onPress={() => onContactPress?.(contact)}
        activeOpacity={0.7}
      >
        <View style={styles.leftSection}>
          <Image
            source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
            style={styles.avatar}
          />
          {!contact.isGhost && <View style={styles.statusIndicator} />}
        </View>

        <View style={styles.middleSection}>
          <View style={styles.nameContainer}>
            <Text type="bodySmallBold" numberOfLines={1}>
              {contact.fullName}
            </Text>
            {!!degreeLabel && (
              <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                {' · '}{degreeLabel}
              </Text>
            )}
          </View>
          {/* No e-mail here: a suggested person is not a contact yet */}
          {getMutualContactsText(contact.id) && (
            <Text 
              type="bodyExtraSmall" 
              color={theme.colors.secondary}
              numberOfLines={1}
              style={styles.hashtags}
            >
              {getMutualContactsText(contact.id)}
            </Text>
          )}
        </View>

        {!isContact && 
          <View style={styles.rightSection}>
            <AddContactButton
              onPress={() => onAddPress?.(contact)}
              text={isRequestPending ? t('addContactModal.requestSent') : t('common.add')}
              disabled={isRequestPending}
            />
          </View>
        }
      </TouchableOpacity>
    )
  }

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Text type="bodySmallBold" color={theme.colors.secondary}>
        {resolvedEmptyMessage}
      </Text>
    </View>
  )

  const renderSeparator = () => <View style={styles.separator} />

  if (isLoading) {
    return <ContactItemSkeletonList count={8} />
  }

  return (
    <FlatList
      data={contacts}
      renderItem={renderContactItem}
      keyExtractor={(item) => item.id}
      ItemSeparatorComponent={renderSeparator}
      ListEmptyComponent={!isLoading ? renderEmptyState : null}
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
      extraData={contacts.length}
      removeClippedSubviews={false}
      maxToRenderPerBatch={10}
      windowSize={10}
      onEndReached={() => {
        if (!isFetchingNextPage && hasNextPage) {
          fetchNextPage?.()
        }
      }}
      onEndReachedThreshold={0.4}
    />
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexGrow: 1,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      height: ITEM_HEIGHT,
    },
    leftSection: {
      position: 'relative',
      marginRight: theme.spacing.md,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.background,
    },
    statusIndicator: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.colors.green,
      borderWidth: 2,
      borderColor: theme.colors.background,
    },
    ghostIndicator: {
      position: 'absolute',
      top: -3,
      right: -3,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.background,
      alignItems: 'center',
      justifyContent: 'center',
    },
    middleSection: {
      flex: 1,
      justifyContent: 'center',
    },
    hashtags: {
      marginTop: theme.spacing.xs / 2,
    },
    rightSection: {
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: theme.spacing.md,
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: theme.spacing.xxl,
    },
    separator: {
      height: 1,
      backgroundColor: theme.colors.grey,
      marginLeft: AVATAR_SIZE + theme.spacing.md,
    },
    nameContainer: {
      flexDirection: 'row',
      alignItems: 'center'
    }
  }) 